/**
 * One human message for a failed axios request (design evaluation F4).
 *
 * axios rejects with "Request failed with status code 500" or "Network Error",
 * and most call sites toast `${err.message}`. normalizeApiError rewrites that
 * message in place — the server's own `error` / `message` / `detail` when it sent
 * one, else a sentence for the status, timeout or network failure — so every
 * existing toast reads correctly without touching the call site. The axios
 * interceptors in services/api.ts call it on every rejection.
 */

const SERVER_KEYS = ['error', 'message', 'detail', 'details'] as const;

/** The parts of an axios error (or any thrown value) this file reads. */
type ErrorLike = {
  message?: unknown;
  code?: unknown;
  request?: unknown;
  response?: { status?: unknown; data?: unknown };
  __CANCEL__?: unknown;
};

function messageFromBody(body: unknown): string | undefined {
  if (typeof body === 'string') {
    const t = body.trim();
    // A plain-text body is the message; an HTML error page (proxy, nginx) is not.
    return t && t.length <= 300 && !t.startsWith('<') ? t : undefined;
  }
  if (!body || typeof body !== 'object') return undefined;
  const b = body as Record<string, unknown>;
  for (const key of SERVER_KEYS) {
    const v = b[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return undefined;
}

function messageForStatus(status: number): string {
  if (status === 400) return 'The request was rejected (400). Check the values and try again.';
  if (status === 401) return 'Your session has expired. Sign in again.';
  if (status === 403) return "You don't have permission to do this (403).";
  if (status === 404) return 'Not found (404). It may have been deleted.';
  if (status === 409) return 'This conflicts with the current state (409). Refresh and try again.';
  if (status === 429) return 'Too many requests (429). Wait a moment and try again.';
  if (status >= 500) return `The server hit an error (${status}). Try again shortly.`;
  return `Request failed (${status}).`;
}

/** The message an axios error should carry, or undefined to leave it as is. */
export function apiErrorText(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const e = error as ErrorLike;
  if (e.__CANCEL__ || e.code === 'ERR_CANCELED') return undefined;

  const res = e.response;
  if (res) return messageFromBody(res.data) ?? messageForStatus(Number(res.status) || 0);

  if (e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT' || /timeout/i.test(String(e.message ?? ''))) {
    return "The server didn't respond in time. Try again.";
  }
  if (e.request || e.code === 'ERR_NETWORK' || e.message === 'Network Error') {
    return "Can't reach the server. Check your connection and try again.";
  }
  return undefined;
}

/** Rewrites an axios error's `message` in place (see the file comment). Returns the error. */
export function normalizeApiError<T>(error: T): T {
  const text = apiErrorText(error);
  if (text && error && typeof error === 'object') {
    try {
      (error as ErrorLike).message = text;
    } catch {
      /* frozen error object: leave it */
    }
  }
  return error;
}

/** A message for any thrown value: server text, then the error's message, then `fallback`. */
export function apiErrorMessage(error: unknown, fallback = 'Something went wrong. Try again.'): string {
  const text = apiErrorText(error);
  if (text) return text;
  const m = (error as ErrorLike | undefined)?.message;
  return typeof m === 'string' && m.trim() ? m : fallback;
}

/** The HTTP status of an axios error, if it got a response. */
export function httpStatus(error: unknown): number | undefined {
  const s = (error as ErrorLike | undefined)?.response?.status;
  return typeof s === 'number' ? s : undefined;
}

/** True for a 404: the record really does not exist (anything else is a load error). */
export function isNotFoundError(error: unknown): boolean {
  return httpStatus(error) === 404;
}

/** react-query `retry` for a detail load: a 404 or 403 will not change on retry. */
export function retryUnlessClientError(failureCount: number, error: unknown): boolean {
  const s = httpStatus(error);
  if (s !== undefined && s >= 400 && s < 500 && s !== 408 && s !== 429) return false;
  return failureCount < 3;
}
